import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { X, Play, Loader } from "lucide-react";
import SEO from "../components/SEO";

const CRM = process.env.REACT_APP_CRM_BACKEND;
const CRM_KEY = process.env.REACT_APP_CRM_API_KEY;

// Resolve CRM media URLs directly — no backend proxy needed, CRM media is publicly accessible.
const resolveUrl = (url) => {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  return `${CRM}${url.startsWith("/") ? "" : "/"}${url}`;
};

// Auto-generates a thumbnail by seeking to 1s of the video
const VideoCard = ({ video, onClick }) => {
  const videoRef = useRef(null);
  const handleLoadedMetadata = () => {
    if (videoRef.current) videoRef.current.currentTime = 1;
  };
  return (
    <div
      onClick={onClick}
      data-testid="gallery-video-card"
      className="group relative rounded-2xl overflow-hidden bg-gray-800 shadow-lg cursor-pointer"
      style={{ aspectRatio: "16/9" }}
    >
      <video
        ref={videoRef}
        src={video.url}
        muted
        preload="metadata"
        onLoadedMetadata={handleLoadedMetadata}
        className="w-full h-full object-cover opacity-75 group-hover:opacity-90 transition-opacity"
      />
      <div className="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors flex items-center justify-center">
        <div className="w-14 h-14 bg-white/90 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg">
          <Play size={20} className="text-[#9B1522] ml-1" />
        </div>
      </div>
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
        <p className="text-white font-bold text-sm truncate">{video.title}</p>
        {video.caption && <p className="text-gray-300 text-xs mt-0.5 truncate">{video.caption}</p>}
      </div>
    </div>
  );
};

const Gallery = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState("All");
  const [lightbox, setLightbox] = useState(null);
  const [videoModal, setVideoModal] = useState(null);

  useEffect(() => {
    axios
      .get(`${CRM}/api/public/gallery/tipsblr`, { headers: { "X-API-Key": CRM_KEY } })
      .then((res) => {
        if (Array.isArray(res.data) && res.data.length > 0) {
          const normalized = res.data
            .filter((item) => item.type === "photo" || item.type === "video")
            .map((item) => ({ ...item, url: resolveUrl(item.url) }));
          setItems(normalized.length > 0 ? normalized : []);
        } else {
          setItems([]);
        }
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  const videos = items.filter((i) => i.type === "video");
  const photos = items.filter((i) => i.type === "photo");
  const tags = ["All", ...new Set(photos.map((i) => i.tag).filter(Boolean))];
  const filteredPhotos = active === "All" ? photos : photos.filter((i) => i.tag === active);

  return (
    <main>
      <SEO
        title="Gallery | Campus Life & School Moments"
        description="Explore TIPS Bengaluru's photo gallery – vibrant campus life, academic achievements, sports, cultural events and classroom moments across 6 Bangalore campuses."
        keywords="TIPS Bengaluru gallery, CBSE school Bangalore photos, school events gallery, campus life Bangalore school, Indian Public School Bengaluru"
        canonical="/gallery"
      />

      {/* Page Header */}
      <section className="bg-[#1A0505] py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 text-sm text-gray-400 mb-4">
            <Link to="/" className="hover:text-[#C83C4A] transition-colors">Home</Link>
            <span>/</span>
            <span className="text-white">Gallery</span>
          </div>
          <span className="text-[#C83C4A] text-xs font-bold uppercase tracking-widest">Our Story in Images</span>
          <h1 className="text-4xl sm:text-5xl font-bold text-white mt-2" style={{ fontFamily: "'Poppins', sans-serif" }}>Photo &amp; Video Gallery</h1>
          <p className="text-gray-400 mt-3 max-w-xl">Sports days, annual celebrations, field trips, campus life — moments that define the TIPS experience.</p>
        </div>
      </section>

      {loading ? (
        <section className="py-24 bg-gray-50 flex justify-center">
          <Loader size={32} className="animate-spin text-[#B41C2C]" />
        </section>
      ) : items.length === 0 ? (
        <section className="py-24 bg-gray-50">
          <div className="max-w-md mx-auto text-center px-4">
            <div className="w-16 h-16 bg-[#FEE7E9] rounded-full flex items-center justify-center mx-auto mb-4">
              <Play size={24} className="text-[#B41C2C]" />
            </div>
            <h3 className="text-lg font-bold text-gray-800 mb-2">Gallery Coming Soon</h3>
            <p className="text-sm text-gray-500">Our gallery is being updated. Check back soon for campus life moments.</p>
          </div>
        </section>
      ) : (
        <>
          {/* Category Filter Tabs */}
          {photos.length > 0 && (
            <div className="bg-white border-b border-gray-100 sticky top-0 z-10">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 overflow-x-auto">
                <div className="flex gap-0 min-w-max">
                  {tags.map((tag) => (
                    <button
                      key={tag}
                      onClick={() => setActive(tag)}
                      className={`px-6 py-4 text-sm font-semibold border-b-2 whitespace-nowrap transition-colors ${
                        active === tag
                          ? "border-[#9B1522] text-[#9B1522]"
                          : "border-transparent text-gray-500 hover:text-gray-800"
                      }`}
                    >
                      {tag}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Photo Gallery */}
          {photos.length > 0 && (
            <section className="py-12 bg-white">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="mb-8">
                  <span className="text-[#B41C2C] text-xs font-bold uppercase tracking-widest">Campus Moments</span>
                  <h2 className="text-2xl font-bold text-gray-900 mt-1" style={{ fontFamily: "'Poppins', sans-serif" }}>Photo Gallery</h2>
                </div>
                {filteredPhotos.length === 0 ? (
                  <div className="text-center py-12 text-gray-400">No photos in this category yet.</div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                    {filteredPhotos.map((img) => (
                      <div
                        key={img.id}
                        className="group relative rounded-xl overflow-hidden cursor-pointer bg-gray-200"
                        style={{ aspectRatio: "4/3" }}
                        onClick={() => setLightbox(img)}
                        data-testid="gallery-card"
                      >
                        <img
                          src={img.url}
                          alt={img.title}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                          onError={(e) => { e.target.parentElement.style.display = "none"; }}
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-end p-3">
                          <span className="text-white text-xs font-semibold opacity-0 group-hover:opacity-100 transition-opacity bg-black/50 px-2 py-1 rounded-full">
                            {img.title}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Video Gallery */}
          {videos.length > 0 && (
            <section className="py-14 bg-gray-900">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="mb-8">
                  <span className="text-[#C83C4A] text-xs font-bold uppercase tracking-widest">See TIPS in Action</span>
                  <h2 className="text-2xl font-bold text-white mt-1" style={{ fontFamily: "'Poppins', sans-serif" }}>Video Gallery</h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {videos.map((video) => (
                    <VideoCard key={video.id} video={video} onClick={() => setVideoModal(video)} />
                  ))}
                </div>
              </div>
            </section>
          )}
        </>
      )}

      {/* Photo Lightbox */}
      {lightbox && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4" onClick={() => setLightbox(null)}>
          <div className="relative max-w-3xl w-full" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setLightbox(null)} className="absolute -top-10 right-0 text-white hover:text-gray-300">
              <X size={28} />
            </button>
            <img src={lightbox.url} alt={lightbox.title} className="w-full rounded-xl shadow-2xl" />
            <p className="text-white text-center mt-3 text-sm">{lightbox.title}</p>
            {lightbox.caption && <p className="text-gray-400 text-center text-xs mt-1">{lightbox.caption}</p>}
          </div>
        </div>
      )}

      {/* Video Modal */}
      {videoModal && (
        <div className="fixed inset-0 bg-black/90 z-[70] flex items-center justify-center p-4" onClick={() => setVideoModal(null)}>
          <div className="relative w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setVideoModal(null)} className="absolute -top-10 right-0 text-white hover:text-gray-300 transition-colors">
              <X size={28} />
            </button>
            <video src={videoModal.url} controls autoPlay className="w-full rounded-xl shadow-2xl bg-black" style={{ maxHeight: "75vh" }} />
            <p className="text-white text-center mt-3 text-sm font-semibold">{videoModal.title}</p>
            {videoModal.caption && <p className="text-gray-400 text-center text-xs mt-1">{videoModal.caption}</p>}
          </div>
        </div>
      )}

      {/* CTA */}
      <section className="py-14 bg-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4" style={{ fontFamily: "'Poppins', sans-serif" }}>A Glimpse into Life at TIPS</h2>
          <p className="text-gray-500 leading-relaxed mb-6">
            At The Indian Public School, every day is an opportunity for discovery. From science fairs to cultural performances, sports competitions to art exhibitions — our students live vibrant, enriching lives.
          </p>
          <Link to="/admissions" className="inline-flex items-center bg-[#9B1522] hover:bg-[#87111E] text-white font-semibold px-7 py-3 rounded-xl transition-colors">
            Join Our Community
          </Link>
        </div>
      </section>
    </main>
  );
};

export default Gallery;